import React, { useState } from "react";
import { Modal, Upload, Button, Alert, Typography, Divider } from "antd";
import {
  InboxOutlined,
  DownloadOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { toast } from "react-hot-toast";
import CSVPreviewModal from "./CSVPreviewModal";

const { Dragger } = Upload;
const { Title, Text } = Typography;

const BulkCustomerUpload = ({ visible, onCancel, onUploadSuccess }) => {
  const [fileList, setFileList] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [csvData, setCsvData] = useState([]);
  const [showPreview, setShowPreview] = useState(false);

  // Parse CSV file
  const parseCSV = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const text = e.target.result;
          const lines = text.split("\n").filter((line) => line.trim() !== "");

          if (lines.length < 2) {
            reject(
              new Error(
                "CSV file must have at least a header row and one data row"
              )
            );
            return;
          }

          const headers = lines[0]
            .split(",")
            .map((header) => header.trim().replace(/"/g, ""));
          const data = [];

          for (let i = 1; i < lines.length; i++) {
            const values = lines[i]
              .split(",")
              .map((value) => value.trim().replace(/"/g, ""));
            const row = {};

            headers.forEach((header, index) => {
              row[header] = values[index] || "";
            });

            data.push(row);
          }

          resolve(data);
        } catch (error) {
          reject(
            new Error("Failed to parse CSV file. Please check the format.")
          );
        }
      };
      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.readAsText(file);
    });
  };

  const handleUpload = async () => {
    if (csvData.length === 0) {
      toast.error("No data to upload");
      return;
    }

    setUploading(true);
    try {
      // Replace with your actual API endpoint
      // const response = await axios.post('/customer/bulk-upload', {
      //   customers: csvData
      // });

      // Simulate API call for now
      await new Promise((resolve) => setTimeout(resolve, 2000));

      toast.success(`${csvData.length} customers uploaded successfully!`);
      handleCancel();
      onUploadSuccess?.();
    } catch (error) {
      toast.error("Failed to upload customers. Please try again.");
      console.error("Upload error:", error);
    } finally {
      setUploading(false);
    }
  };

  const uploadProps = {
    name: "file",
    multiple: false,
    accept: ".csv",
    fileList,
    beforeUpload: async (file) => {
      const isCSV = file.type === "text/csv" || file.name.endsWith(".csv");
      if (!isCSV) {
        toast.error("You can only upload CSV files!");
        return false;
      }
      const isLt2M = file.size / 1024 / 1024 < 2;
      if (!isLt2M) {
        toast.error("File must be smaller than 2MB!");
        return false;
      }

      try {
        const parsedData = await parseCSV(file);
        setCsvData(parsedData);
        setFileList([file]);
        setShowPreview(true);
      } catch (error) {
        toast.error(error.message);
        return false;
      }

      return false; // Prevent automatic upload
    },
    onRemove: () => {
      setFileList([]);
    },
    onDrop(e) {
      console.log("Dropped files", e.dataTransfer.files);
    },
  };

  const downloadSampleCSV = () => {
    // Sample CSV data
    const csvContent = `Name,Email,Phone,Address,Account Number,Meter Number
John Doe,john.doe@example.com,+1234567890,123 Main St,ACC001,MTR001
Jane Smith,jane.smith@example.com,+0987654321,456 Oak Ave,ACC002,MTR002
Bob Johnson,bob.johnson@example.com,+1122334455,789 Pine Rd,ACC003,MTR003`;

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "customer_upload_template.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCancel = () => {
    setFileList([]);
    setCsvData([]);
    setShowPreview(false);
    onCancel();
  };

  return (
    <>
      <Modal
        title="Bulk Customer Upload"
        open={visible}
        onCancel={handleCancel}
        width={600}
        footer={[
          <Button key="cancel" onClick={handleCancel}>
            Cancel
          </Button>,
          <Button
            key="upload"
            type="primary"
            loading={uploading}
            onClick={() => setShowPreview(true)}
            disabled={fileList.length === 0}
            icon={<UploadOutlined />}
          >
            Preview & Upload
          </Button>,
        ]}
      >
        <div className="">
          <div className="flex mb-2 justify-between items-center">
            <Title level={5} className="!mb-0">
              Upload CSV File
            </Title>
          </div>

          <Dragger {...uploadProps} className="!p-6">
            <p className="ant-upload-drag-icon">
              <InboxOutlined style={{ fontSize: "48px", color: "#1890ff" }} />
            </p>
            <p className="ant-upload-text text-lg font-medium">
              Click or drag CSV file to this area to upload
            </p>
            <p className="ant-upload-hint">
              Support for single CSV file upload. File size should not exceed
              2MB.
            </p>
          </Dragger>

          {fileList.length > 0 && (
            <div className="bg-gray-50 p-3 rounded mt-3">
              <Text strong>Selected file: </Text>
              <Text>{fileList[0].name}</Text>
              <br />
              <Text type="secondary">
                Size: {(fileList[0].size / 1024).toFixed(2)} KB
              </Text>
              {csvData.length > 0 && (
                <>
                  <br />
                  <Text type="secondary">Rows detected: {csvData.length}</Text>
                </>
              )}
            </div>
          )}

          <Divider />

          {/* <div>
            <Title level={5}>CSV Format Requirements:</Title>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Required columns: Name, Email, Phone, Address</li>
              <li>• Optional columns: Account Number, Meter Number</li>
              <li>• First row should contain column headers</li>
              <li>• Use comma (,) as delimiter</li>
              <li>• Ensure email addresses are valid</li>
              <li>• Phone numbers should include country code</li>
            </ul>
          </div> */}
        </div>
      </Modal>

      <CSVPreviewModal
        visible={showPreview}
        onCancel={() => setShowPreview(false)}
        onConfirm={handleUpload}
        csvData={csvData}
        fileName={fileList[0]?.name}
        loading={uploading}
      />
    </>
  );
};

export default BulkCustomerUpload;
